import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection, doc, onSnapshot, runTransaction, serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";

// ─────────────────────────────────────────────────────────────
// Carte café partenaire — 5 cafés achetés, le 6e offert.
//
// Le café garde une carte (QR + tag NFC) qui pointe vers :
//   https://member.westudy.tn/cafe?k=<CODE>
// Scanner la carte = +1 tampon. Avec 5 tampons, le scan suivant
// = café offert, et la carte repart à zéro.
//
//   cafe_codes/{code}   { active: true }           (géré par l'admin)
//   cafe_cards/{uid}    { stamps, totalStamps, totalFree, userName, lastAt }
//   cafe_events/{id}    { type: "stamp"|"free", userId, userName, code, at }
// ─────────────────────────────────────────────────────────────

const STAMPS_FOR_FREE = 5;

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#e9eef5", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", coffee: "#92400e", coffeeBg: "#fef3c7", coffeeBorder: "#fcd34d",
    green: "#16a34a", red: "#dc2626", redBg: "#fef2f2",
    cardBg: "#ffffff", shadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", coffee: "#fbbf24", coffeeBg: "#2a1e07", coffeeBorder: "#92400e",
    green: "#4ade80", red: "#f87171", redBg: "#2d1515",
    cardBg: "#1e293b", shadow: "0 1px 3px rgba(0,0,0,0.4)",
  },
};

async function scanCode(code, user) {
  const codeRef  = doc(db, "cafe_codes", code);
  const cardRef  = doc(db, "cafe_cards", user.uid);
  const eventRef = doc(collection(db, "cafe_events"));
  const userName = user.displayName || user.email || "Membre";

  return runTransaction(db, async (tx) => {
    const codeSnap = await tx.get(codeRef);
    if (!codeSnap.exists() || codeSnap.data().active !== true) return { type: "invalid" };

    const cardSnap = await tx.get(cardRef);
    const card = cardSnap.exists() ? cardSnap.data() : {};
    const stamps = card.stamps || 0;
    const isFree = stamps >= STAMPS_FOR_FREE;
    const newStamps = isFree ? 0 : stamps + 1;

    tx.set(cardRef, {
      stamps: newStamps,
      totalStamps: (card.totalStamps || 0) + (isFree ? 0 : 1),
      totalFree: (card.totalFree || 0) + (isFree ? 1 : 0),
      userName,
      lastAt: serverTimestamp(),
    }, { merge: true });

    tx.set(eventRef, {
      type: isFree ? "free" : "stamp",
      userId: user.uid, userName, code,
      at: serverTimestamp(),
    });

    return { type: isFree ? "free" : "stamp", stamps: newStamps };
  });
}

function LiveClock({ color }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <div style={{ fontSize: 15, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
      {now.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} · {now.toLocaleTimeString("fr-FR")}
    </div>
  );
}

function Cups({ stamps, t, big }) {
  const size = big ? 52 : 44;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, maxWidth: 260, margin: "0 auto" }}>
      {Array.from({ length: STAMPS_FOR_FREE + 1 }).map((_, i) => {
        const isGift = i === STAMPS_FOR_FREE;
        const filled = isGift ? stamps >= STAMPS_FOR_FREE : i < stamps;
        return (
          <div key={i} style={{
            aspectRatio: "1/1", borderRadius: 16,
            border: `2px ${filled ? "solid" : "dashed"} ${filled ? t.coffee : t.border}`,
            background: filled ? t.coffeeBg : t.cardBg,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            fontSize: size * 0.55, transition: "all 0.3s",
            animation: filled && i === stamps - 1 ? "pop 0.4s ease" : "none",
          }}>
            <span style={{ opacity: filled ? 1 : 0.25 }}>{isGift ? "🎁" : "☕"}</span>
            {isGift && <span style={{ fontSize: 9, fontWeight: 800, color: filled ? t.coffee : t.text3, marginTop: 2 }}>OFFERT</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function CafePage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [user, setUser] = useState(auth.currentUser);
  const [card, setCard] = useState(null);
  const [result, setResult] = useState(null); // {type:'stamp'|'free'|'invalid'|'error', stamps}
  const [scanning, setScanning] = useState(false);
  const handledRef = useRef(null);
  const t = dark ? theme.dark : theme.light;

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, "cafe_cards", user.uid),
      s => setCard(s.exists() ? s.data() : { stamps: 0 }), console.error);
  }, [user]);

  // Scan : la page est ouverte avec ?k=CODE
  const code = params.get("k");
  useEffect(() => {
    if (!user || !code || handledRef.current === code) return;
    handledRef.current = code;
    setParams({}, { replace: true }); // un rafraîchissement ne retamponne pas
    setScanning(true);
    scanCode(code, user)
      .then(setResult)
      .catch(e => { console.error(e); setResult({ type: "error" }); })
      .finally(() => setScanning(false));
  }, [user, code]); // eslint-disable-line react-hooks/exhaustive-deps

  const stamps = card?.stamps ?? 0;
  const freeReady = stamps >= STAMPS_FOR_FREE;

  return (
    <div style={{ background: result?.type === "free" ? t.green : t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", transition: "background 0.4s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop { 0%{transform:scale(0.6);opacity:0} 70%{transform:scale(1.12)} 100%{transform:scale(1);opacity:1} }
        @keyframes wiggle { 0%,100%{transform:rotate(-6deg) scale(1)} 50%{transform:rotate(6deg) scale(1.08)} }
        @keyframes steam { 0%{opacity:0;transform:translateY(6px)} 50%{opacity:1} 100%{opacity:0;transform:translateY(-14px)} }
      `}</style>

      <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 18px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        {result?.type !== "free" && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 28, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.bg3, border: `1px solid ${t.border}` }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.blue, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
              </button>
              <button onClick={() => navigate("/")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
            </div>
          </div>
        )}

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 60, textAlign: "center" }}>

          {(scanning || !card) && <div style={{ color: t.text3, fontSize: 13 }}>{scanning ? "Ajout du tampon…" : "Chargement…"}</div>}

          {/* ── CAFÉ OFFERT : écran à montrer au serveur ── */}
          {!scanning && result?.type === "free" && (
            <div style={{ animation: "fadeIn 0.3s ease", color: "#fff" }}>
              <div style={{ fontSize: 96, lineHeight: 1, marginBottom: 12, display: "inline-block", animation: "wiggle 1.2s ease-in-out infinite" }}>☕</div>
              <div style={{ fontSize: 34, fontWeight: 900, letterSpacing: "0.02em", marginBottom: 6 }}>CAFÉ OFFERT</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 18 }}>{user?.displayName || user?.email}</div>
              <div style={{ display: "inline-block", background: "rgba(255,255,255,0.18)", borderRadius: 14, padding: "10px 18px", marginBottom: 28 }}>
                <LiveClock color="#fff" />
              </div>
              <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 28 }}>Montrez cet écran au serveur.</div>
              <button onClick={() => setResult(null)} style={{ border: "2px solid #fff", borderRadius: 14, background: "transparent", color: "#fff", fontSize: 15, fontWeight: 700, padding: "12px 28px", cursor: "pointer" }}>
                Terminé
              </button>
            </div>
          )}

          {/* ── TAMPON AJOUTÉ ── */}
          {!scanning && card && result?.type === "stamp" && (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              <div style={{ position: "relative", display: "inline-block", fontSize: 64, marginBottom: 6, animation: "pop 0.5s ease" }}>
                ☕
                <span style={{ position: "absolute", top: -14, left: "50%", fontSize: 22, animation: "steam 1.6s ease-in-out infinite" }}>〰</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 900, color: t.coffee, marginBottom: 4 }}>+1 café</div>
              <div style={{ marginBottom: 20 }}><LiveClock color={t.text2} /></div>
              <Cups stamps={result.stamps} t={t} big />
              <div style={{ fontSize: 15, fontWeight: 700, color: t.text1, marginTop: 20 }}>
                {result.stamps >= STAMPS_FOR_FREE
                  ? "🎉 Votre prochain café est offert !"
                  : `Encore ${STAMPS_FOR_FREE - result.stamps} café${STAMPS_FOR_FREE - result.stamps > 1 ? "s" : ""} avant le café offert`}
              </div>
              <button onClick={() => setResult(null)} style={{ marginTop: 24, border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>OK</button>
            </div>
          )}

          {/* ── CODE INVALIDE / ERREUR ── */}
          {!scanning && (result?.type === "invalid" || result?.type === "error") && (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: t.red, marginBottom: 8 }}>
                {result.type === "invalid" ? "Carte non valide" : "Erreur"}
              </div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 24 }}>
                {result.type === "invalid" ? "Cette carte n'est plus active. Demandez au serveur." : "Vérifiez votre connexion et rescannez la carte."}
              </div>
              <button onClick={() => setResult(null)} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>OK</button>
            </div>
          )}

          {/* ── CARTE (ouverture normale) ── */}
          {!scanning && card && !result && (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 4 }}>☕ Carte café</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 22 }}>5 cafés achetés = le 6e offert</div>
              <Cups stamps={stamps} t={t} />
              <div style={{
                marginTop: 22, background: freeReady ? t.coffeeBg : t.cardBg,
                border: `1.5px solid ${freeReady ? t.coffeeBorder : t.border}`,
                borderRadius: 14, padding: "14px 16px", fontSize: 13, color: freeReady ? t.coffee : t.text2, lineHeight: 1.6,
              }}>
                {freeReady
                  ? <><strong>🎉 Votre prochain café est offert !</strong><br />Prévenez le serveur, puis scannez la carte du café.</>
                  : <>À chaque café acheté, demandez la <strong style={{ color: t.text1 }}>carte WeStudy</strong> au serveur et scannez le QR code ou approchez votre téléphone (NFC).</>}
              </div>
              {(card.totalFree || 0) > 0 && (
                <div style={{ fontSize: 11, color: t.text3, marginTop: 14 }}>
                  {card.totalFree} café{card.totalFree > 1 ? "s" : ""} offert{card.totalFree > 1 ? "s" : ""} depuis le début
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
